import { type RefObject, useCallback, useEffect, useLayoutEffect } from "react";
import { TAB_CONSTANTS } from "./constants";
import type { TabItem } from "./types";
import { getActiveButton } from "./utils";

type UseTabsIndicatorProps = {
  items: TabItem[];
  activeIndex: number;
  rtl: boolean;
  tabButtonRefs: RefObject<Map<string, HTMLButtonElement>>;
  isActiveTabHidden: boolean;
  indicatorRef: RefObject<HTMLDivElement | null>;
  underlineRef: RefObject<HTMLDivElement | null>;
  scrollRef: RefObject<HTMLDivElement | null>;
};

export function useTabsIndicator({
  items,
  activeIndex,
  rtl,
  tabButtonRefs,
  isActiveTabHidden,
  indicatorRef,
  underlineRef,
  scrollRef,
}: UseTabsIndicatorProps): void {
  const normalizeRTLScroll = useCallback((container: HTMLElement): number => {
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    if (scrollLeft <= 0) {
      return Math.abs(scrollLeft);
    } else if (
      scrollLeft > 0 &&
      Math.abs(scrollWidth - scrollLeft - clientWidth) < 1
    ) {
      return scrollLeft;
    } else {
      return scrollWidth - clientWidth - scrollLeft;
    }
  }, []);

  const setIndicator = useCallback(() => {
    const indicator = indicatorRef.current;
    const container = scrollRef.current;
    if (!indicator || !container) return;

    // If active tab is hidden, position indicator under More button
    if (isActiveTabHidden) {
      const containerWidth = container.clientWidth;
      const moreButtonWidth = TAB_CONSTANTS.MORE_BUTTON_WIDTH;

      // Position at the end, 40px wide
      const tx = rtl
        ? -(containerWidth - moreButtonWidth)
        : containerWidth - moreButtonWidth;

      indicator.style.transform = `translate3d(${tx}px,0,0)`;
      indicator.style.width = `${moreButtonWidth}px`;

      if (!indicator.hasAttribute("data-initialized")) {
        indicator.setAttribute("data-initialized", "true");
      }
      return;
    }

    // Normal behavior - position under active button
    const button = getActiveButton(activeIndex, items, tabButtonRefs);
    if (!button) return;

    const b = button.getBoundingClientRect();
    const c = container.getBoundingClientRect();

    const scrollLeft = rtl
      ? normalizeRTLScroll(container)
      : container.scrollLeft;

    const xFromAnchor = rtl
      ? c.right - b.right + scrollLeft
      : b.left - c.left + scrollLeft;

    const tx = rtl ? -xFromAnchor : xFromAnchor;

    indicator.style.transform = `translate3d(${tx}px,0,0)`;
    indicator.style.width = `${b.width}px`;

    if (!indicator.hasAttribute("data-initialized")) {
      indicator.setAttribute("data-initialized", "true");
    }
  }, [
    activeIndex,
    items,
    tabButtonRefs,
    rtl,
    normalizeRTLScroll,
    indicatorRef,
    scrollRef,
    isActiveTabHidden,
  ]);

  const updateUnderlineWidth = useCallback(() => {
    const underline = underlineRef.current;
    const container = scrollRef.current;
    if (!underline || !container) return;
    underline.style.width = `${container.scrollWidth}px`;
  }, [underlineRef, scrollRef]);

  useLayoutEffect(() => {
    updateUnderlineWidth();
    setIndicator();
  }, [activeIndex, items, setIndicator, updateUnderlineWidth]);

  useEffect(() => {
    const container = scrollRef.current;
    const button = getActiveButton(activeIndex, items, tabButtonRefs);
    if (!container) return;

    const ro = new ResizeObserver(() => {
      updateUnderlineWidth();
      setIndicator();
    });

    const handleScroll = () => {
      setIndicator();
    };

    ro.observe(container);
    if (button) ro.observe(button);

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      ro.disconnect();
      container.removeEventListener("scroll", handleScroll);
    };
  }, [
    activeIndex,
    items,
    tabButtonRefs,
    setIndicator,
    updateUnderlineWidth,
    scrollRef,
  ]);
}
